import type {ApprovedWritingGuideRelease} from '../../vendor/src/features/remotion-editor/editor/items/code/approved-writing-guide-release';
import {AUTHORING_WRITING_GUIDE_PLAYBACK_CONTRACT} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';

export type TrustedWritingPlayback = {
  playbackContract: typeof AUTHORING_WRITING_GUIDE_PLAYBACK_CONTRACT;
  version: string;
  frameToProgress?: (frame: number, totalFrames: number) => number;
  renderWordFrameToCanvas: (canvas: HTMLCanvasElement, runtime: Record<string, unknown>, options: Record<string, unknown>) => unknown;
};

const MAX_RENDERER_BYTES = 1024 * 1024;
const verifiedPlayback = new Map<string, Promise<TrustedWritingPlayback>>();
let registration: Promise<unknown> = Promise.resolve();

async function boundedBytes(response: Response): Promise<Uint8Array<ArrayBuffer>> {
  if (!response.ok || !response.body) throw Error('승인된 따라쓰기 렌더러를 읽지 못했습니다.');
  const reader = response.body.getReader();
  const parts: Uint8Array[] = [];
  let length = 0;
  try {
    while (true) {
      const {done, value} = await reader.read();
      if (done) break;
      length += value.byteLength;
      if (length > MAX_RENDERER_BYTES) {
        await reader.cancel();
        throw Error('따라쓰기 렌더러 크기가 허용 범위를 넘었습니다.');
      }
      parts.push(value);
    }
  } finally { reader.releaseLock(); }
  const result = new Uint8Array(new ArrayBuffer(length));
  let offset = 0;
  for (const part of parts) { result.set(part, offset); offset += part.length; }
  return result;
}

async function digest(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  if (!globalThis.crypto?.subtle) throw Error('따라쓰기 렌더러 바이트 검증을 사용할 수 없습니다.');
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return `sha256:${Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

function registerVerifiedRenderer(bytes: Uint8Array<ArrayBuffer>, release: ApprovedWritingGuideRelease, signal:AbortSignal): Promise<TrustedWritingPlayback> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(signal.reason); return; }
    const scope = globalThis as typeof globalThis & {AlphabetAuthoringPlayback?: TrustedWritingPlayback} & Record<string,unknown>;
    const captureKey=`__wakaApprovedGuideCapture_${crypto.randomUUID().replaceAll('-','')}`;
    let captured:TrustedWritingPlayback|undefined;
    scope[captureKey]=(value:TrustedWritingPlayback) => {captured=value;};
    // The renderer bytes were hashed above. This fixed wrapper captures its API
    // and restores the preview global within the same script execution turn.
    const prefix='(function(){const previous=globalThis.AlphabetAuthoringPlayback;try{\n';
    const suffix=`\n;globalThis[${JSON.stringify(captureKey)}](globalThis.AlphabetAuthoringPlayback);`+
      ' }finally{globalThis.AlphabetAuthoringPlayback=previous;}})();';
    const url = URL.createObjectURL(new Blob([prefix,bytes,suffix], {type: 'text/javascript'}));
    const script = document.createElement('script');
    let settled = false;
    const cleanup = () => { clearTimeout(timer); signal.removeEventListener('abort',onAbort); delete scope[captureKey]; script.remove(); URL.revokeObjectURL(url); };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const onAbort=() => fail(Error('승인된 따라쓰기 렌더러 요청 시간이 초과됐습니다.'));
    const timer = setTimeout(() => fail(Error('승인된 따라쓰기 렌더러 로딩 시간이 초과됐습니다.')), 15_000);
    signal.addEventListener('abort',onAbort,{once:true});
    script.addEventListener('load', () => {
      if (settled) return;
      const playback = captured;
      if (!playback ||
          playback.version !== release.rendererVersion ||
          playback.playbackContract !== release.playbackContract ||
          typeof playback.renderWordFrameToCanvas !== 'function') {
        fail(Error('승인된 따라쓰기 렌더러 계약이 일치하지 않습니다.'));
        return;
      }
      settled = true;
      cleanup();
      // Capture this version. Other editor previews may later replace the global.
      resolve(playback);
    }, {once: true});
    script.addEventListener('error', () => fail(Error('승인된 따라쓰기 렌더러 실행에 실패했습니다.')), {once: true});
    script.src = url;
    document.head.append(script);
  });
}

/** Hash the actual shipped script before any execution, and register versions serially. */
export function loadTrustedWritingPlayback(release: ApprovedWritingGuideRelease, assetUrl: string): Promise<TrustedWritingPlayback> {
  const key = `${release.rendererAssetPath}:${release.rendererSourceHash}`;
  const cached = verifiedPlayback.get(key);
  if (cached) return cached;
  const load = (async () => {
    const controller=new AbortController();
    let timer:ReturnType<typeof setTimeout>|undefined;
    const timeout=new Promise<never>((_,reject) => {
      timer=setTimeout(() => {
        controller.abort();
        reject(Error('승인된 따라쓰기 렌더러 요청 시간이 초과됐습니다.'));
      },15_000);
    });
    try {
      return await Promise.race([(async () => {
        const response = await fetch(assetUrl, {cache: 'no-store',signal:controller.signal});
        const bytes = await boundedBytes(response);
        controller.signal.throwIfAborted();
        if (await digest(bytes) !== release.rendererSourceHash)
          throw Error('따라쓰기 렌더러 바이트 해시가 승인본과 다릅니다.');
        controller.signal.throwIfAborted();
        const next = registration.then(() => registerVerifiedRenderer(bytes, release, controller.signal));
        registration = next.catch(() => undefined);
        return next;
      })(),timeout]);
    } finally { if (timer) clearTimeout(timer); }
  })().catch(error => { verifiedPlayback.delete(key); throw error; });
  verifiedPlayback.set(key, load);
  return load;
}
